import type { ReleaseNote } from './registry';

// Public changelog data — maintained NEWEST-FIRST (prepend one object per ship;
// see PROPOSED_IA §4). Backfilled from analysis/clipradar-release-history.md.
//
// Scope rule applied: a version appears here ONLY if it (a) has a bundled
// frontend/src/features/updates/notes/<version>.md file AND (b) the release
// history shows real evidence it reached testers (a memory ledger "SHIPPED"
// note, a live post-ship incident against the installed app, etc.) — not just
// a package.json version bump. Versions excluded on that basis, per
// release-history.md §1/§4:
//   - 1.0.0-beta.1 (has a notes file, but no package.json commit ever set this
//     exact version string — its content likely describes work that actually
//     shipped under a later version number; TODO(founder) to confirm which).
//   - 1.0.0-beta.2, 1.0.6-beta.1 (package.json bump only, no notes file, no
//     shipped evidence — dev churn).
//   - 1.0.5-beta (evidence of an internal/installed build only; public tester
//     distribution unconfirmed).
//   - 1.1.0-beta.1 (prepared, then explicitly paused/superseded same-day by
//     1.1.0-beta.2 — never went out on its own; its real work shipped under
//     later version numbers instead).
export const RELEASES: ReleaseNote[] = [
  {
    version: '1.1.0-beta.5',
    date: '2026-07-12',
    hook: "Crashes, flaky drives, and re-analyzing a clip can no longer quietly break a source.",
    shipped: true,
    Improved: [
      'Analysis no longer hangs forever on an unresponsive network or USB drive — it gives up cleanly and tells you why, instead of freezing the whole queue.',
      'Clips and files with non-Latin names (for example Cyrillic) now analyze and export reliably.',
    ],
    Fixed: [
      'If ClipRadar or your PC is interrupted mid-analysis — crash, power loss, force-quit — a source no longer ends up marked "analyzed" but empty, or silently disappears from your library.',
      "Re-analyzing a clip now keeps its speech status and your per-source settings — excluded-from-search, renames, and hidden clips all survive.",
      'Deleting a source or editing a transcript while an analysis job is running is now safe — no more races that could resurrect a deleted clip or revert an edit.',
    ],
  },
  // TODO(founder): confirm 1.1.0-beta.4 was actually uploaded to R2 (beta.yml
  // pinned to it) before flipping shipped:true — release-history.md found the
  // version bump + a real notes file, but no upload/verification evidence.
  // Until confirmed this entry stays hidden from the public page (ReleaseNotes
  // only renders it once no shipped:true entries exist — drop that Phase-1
  // fallback once this flips, see ReleaseNotes.tsx).
  {
    version: '1.1.0-beta.4',
    date: '2026-07-11',
    hook: "A fresh look for the whole app, plus honest warnings when a file's speech couldn't be analyzed.",
    shipped: false,
    New: [
      'A fresh look for the whole app — new typography, a refreshed accent color and icons, and reworked toolbars, so results, the Media table, and every screen are easier to scan.',
      "ClipRadar now tells you when a file's speech couldn't be analyzed properly (damaged audio, the wrong language, or too little usable text) instead of quietly marking it done. Warning badges in Media explain exactly what's wrong, with one-click fixes like Re-transcribe now.",
      'A summary bar shows how many files need attention, with Show files and Re-check all.',
      "Pin your footage's spoken language in Settings for more reliable transcription of non-English audio.",
    ],
    Improved: [
      'Selection in tables is now checkbox-only, so clicking a row never surprises you.',
      'The search box has a clearer submit button, and Match similar results now jump straight to the matching moment.',
      'Dozens of small polish fixes across Search, Media, People, and the Premiere panel.',
    ],
  },
  // TODO(founder): 1.1.0-beta.2 and 1.1.0-beta.3 both confirmed-shipped but
  // neither has a bundled what's-new note, so testers saw no in-app summary
  // for either — decide whether to keep them as two entries (below) or
  // collapse them into one combined "1.1.0" release note (PROPOSED_IA §5).
  {
    version: '1.1.0-beta.3',
    date: '2026-07-10',
    hook: "Collections can now hold files you haven't analyzed yet.",
    shipped: true,
    New: [
      "Collections can now include files you haven't analyzed yet, so you can start organizing footage before indexing finishes.",
      'Shift-click to select a range of files in the Media table.',
      'Queue more files for analysis while an analysis run is already in progress.',
    ],
    Improved: [
      'The Media table now shows the true date and time a file was added, not just its file timestamp.',
    ],
  },
  {
    version: '1.1.0-beta.2',
    date: '2026-07-10',
    hook: 'Send a bug report straight from the app, with diagnostics attached only if you say so.',
    shipped: true,
    New: [
      'Built-in bug reporting: send a report straight from the app when something looks wrong, with diagnostics attached only if you choose to include them.',
    ],
    Improved: [
      'Better behind-the-scenes error logging, so issues you report can be tracked down faster.',
    ],
  },
  {
    version: '0.9.0',
    date: '2026-06-10',
    hook: "The first beta — search your footage by what's on screen, what's said, and who's in it.",
    shipped: true,
    New: [
      "Search your footage by what's on screen, what's said, and who appears — all running locally on your machine, with nothing uploaded.",
      'Ultra Search understands plain-language queries.',
      'The app now updates itself automatically — new betas will show up right here.',
    ],
    Improved: [
      'Report a bug or request a feature directly from Settings → Help.',
    ],
  },
];
